import { notifications } from '@mantine/notifications';
import { reaction, type IReactionDisposer } from 'mobx';
import type React from 'react';

import { ROUTES } from '@/configs/routes';
import { ORGANIZATION_ANALYSIS_DOCUMENT_ID, normalizeAnalysisDocumentId } from '@/entities/DocumentAnalysis';
import { OrganizationPageDocumentsAnalyzeResultStore } from '@/stores/local/pages/OrganizationPageDocumentsAnalyzeResultStore';
import {
  publishNotificationDismiss,
  runWithDismissSyncGuard,
  shouldSkipDismissBroadcast,
  subscribeAnyExecutionStorage,
  subscribeNotificationDismiss,
  type ExecutionStorageKeyPayload,
} from '@/utils/analysisExecutionStorage';

import type { AnalysesStore } from '../AnalysesStore';
import type { NotificationsStore } from '../NotificationsStore';

type StoreParams = {
  organizationId: string;
  documentId?: string | null;
  analysisTypeId: number;
};

type Entry = {
  store: OrganizationPageDocumentsAnalyzeResultStore;
  refs: number;
  disposeOnIdle?: IReactionDisposer;
};

export class AnalysisExecutionsStore {
  private readonly _analysesStore: AnalysesStore;
  private readonly _notificationsStore: NotificationsStore;
  private readonly _entries = new Map<string, Entry>();
  private _unsubscribeExternalNotifications: (() => void) | null = null;
  private _unsubscribeDismissNotifications: (() => void) | null = null;

  constructor(params: { analysesStore: AnalysesStore; notificationsStore: NotificationsStore }) {
    this._analysesStore = params.analysesStore;
    this._notificationsStore = params.notificationsStore;
    this._subscribeToExternalNotifications();
    this._subscribeToNotificationDismiss();
  }

  getStore(params: StoreParams): OrganizationPageDocumentsAnalyzeResultStore {
    const normalizedDocumentId = normalizeAnalysisDocumentId(params.documentId);
    const key = this._makeKey(params.organizationId, normalizedDocumentId, params.analysisTypeId);
    const existing = this._entries.get(key);

    if (existing) {
      existing.refs += 1;
      existing.disposeOnIdle?.();
      existing.disposeOnIdle = undefined;

      return existing.store;
    }

    const store = new OrganizationPageDocumentsAnalyzeResultStore({
      organizationId: params.organizationId,
      documentId: normalizedDocumentId,
      analysesStore: this._analysesStore,
      notificationsStore: this._notificationsStore,
    });

    this._entries.set(key, { store, refs: 1 });

    return store;
  }

  releaseStore(params: StoreParams): void {
    const normalizedDocumentId = normalizeAnalysisDocumentId(params.documentId);
    const key = this._makeKey(params.organizationId, normalizedDocumentId, params.analysisTypeId);
    const entry = this._entries.get(key);

    if (!entry) {
      return;
    }

    entry.refs = Math.max(0, entry.refs - 1);

    if (entry.refs > 0) {
      return;
    }

    if (entry.store.meta.isLoading) {
      entry.disposeOnIdle?.();
      entry.disposeOnIdle = reaction(
        () => entry.store.meta.isLoading,
        (isLoading) => {
          if (!isLoading && entry.refs === 0) {
            entry.disposeOnIdle?.();
            this._destroyEntry(key);
          }
        },
      );

      return;
    }

    this._destroyEntry(key);
  }

  destroy(): void {
    this._unsubscribeExternalNotifications?.();
    this._unsubscribeExternalNotifications = null;
    this._unsubscribeDismissNotifications?.();
    this._unsubscribeDismissNotifications = null;
    this._entries.forEach((_, key) => {
      this._destroyEntry(key);
    });
    this._entries.clear();
  }

  private _makeKey(organizationId: string, documentId: string, analysisTypeId: number): string {
    return `${organizationId}:${documentId}:${analysisTypeId}`;
  }

  private _makeNotificationId(params: ExecutionStorageKeyPayload): string {
    return `analysis-finished-${params.organizationId}-${params.documentId}-${params.analysisTypeId}`;
  }

  private _subscribeToExternalNotifications(): void {
    this._unsubscribeExternalNotifications = subscribeAnyExecutionStorage(({ key, state }) => {
      if (!state || state.status !== 'completed') {
        return;
      }

      this._notifyAnalysisCompleted(key, state.analysisTitle);
    });
  }

  private _subscribeToNotificationDismiss(): void {
    this._unsubscribeDismissNotifications = subscribeNotificationDismiss((key) => {
      const notificationId = this._makeNotificationId(key);

      runWithDismissSyncGuard(notificationId, () => {
        notifications.hide(notificationId);
      });
    });
  }

  private _notifyAnalysisCompleted(params: ExecutionStorageKeyPayload, analysisTitleFromStorage?: string): void {
    const { organizationId, documentId, analysisTypeId } = params;
    const analysisTitle =
      analysisTitleFromStorage ?? this._getAnalysisTitleById(organizationId, documentId, analysisTypeId);

    const route =
      documentId === ORGANIZATION_ANALYSIS_DOCUMENT_ID
        ? ROUTES.organization.macro.analyze.result.create(organizationId, analysisTypeId)
        : ROUTES.organization.documents.document.analyze.result.create(organizationId, documentId, analysisTypeId);
    const notificationId = this._makeNotificationId(params);
    let isDismissed = false;

    const broadcastDismiss = () => {
      if (isDismissed) {
        return;
      }
      isDismissed = true;

      publishNotificationDismiss(params);
    };

    const handleOpenResult = (): void => {
      window.open(route, '_blank', 'noopener');
      notifications.hide(notificationId);
      broadcastDismiss();
    };

    const isCloseClick = (event?: React.MouseEvent<HTMLElement>): boolean => {
      const native = event?.nativeEvent as Event & { _notificationClosedByButton?: boolean };

      if (native?._notificationClosedByButton) {
        return true;
      }

      const path = native?.composedPath?.() ?? [];

      return path.some((node) => {
        const el = node as HTMLElement | null;

        return el?.dataset?.notificationClose != null || el?.getAttribute?.('data-close-button') != null;
      });
    };

    const handleClose = (): void => {
      if (shouldSkipDismissBroadcast(notificationId)) {
        return;
      }

      broadcastDismiss();
    };

    this._notificationsStore.showSuccessAction({
      title: 'Анализ завершен',
      message: analysisTitle,
      position: 'top-right',
      onClick: (event?: React.MouseEvent<HTMLElement>) => {
        if (event?.defaultPrevented || isCloseClick(event)) {
          return;
        }
        handleOpenResult();
      },
      id: notificationId,
      autoClose: 30000,
      onClose: handleClose,
      closeButtonProps: {
        onClickCapture: (event: React.MouseEvent<HTMLElement>) => {
          const native = event.nativeEvent as Event & { _notificationClosedByButton?: boolean };

          native._notificationClosedByButton = true;
        },
        onMouseDownCapture: (event: React.MouseEvent<HTMLElement>) => {
          const native = event.nativeEvent as Event & { _notificationClosedByButton?: boolean };

          native._notificationClosedByButton = true;
        },
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          const native = event.nativeEvent as Event & { _notificationClosedByButton?: boolean };

          native._notificationClosedByButton = true;
          event.preventDefault();
          event.stopPropagation();
          notifications.hide(notificationId);
          broadcastDismiss();
        },
        onMouseDown: (event: React.MouseEvent<HTMLElement>) => {
          const native = event.nativeEvent as Event & { _notificationClosedByButton?: boolean };

          native._notificationClosedByButton = true;
          event.preventDefault();
          event.stopPropagation();
        },
      },
    });
  }

  private _getAnalysisTitleById(organizationId: string, documentId: string, analysisTypeId: number): string {
    const item = this._analysesStore.getAnalysisById(organizationId, documentId, analysisTypeId);

    return item?.name ?? `Анализ #${analysisTypeId}`;
  }

  private _destroyEntry(key: string): void {
    const entry = this._entries.get(key);

    if (!entry) {
      return;
    }

    entry.disposeOnIdle?.();
    entry.disposeOnIdle = undefined;
    entry.store.destroy();
    this._entries.delete(key);
  }
}
